import { catchError, exhaustMap, firstValueFrom, map, mergeMap, of, retryWhen, throwError } from 'rxjs';

import { APIResponse } from '../../system-libraries/api-types';
import Config from '../config-manager';
import { longTermToken, systemError } from '../events-manager';

type Request<T> = () => Promise<T>;

export const withResponseHandler = async <T>(request: Request<Response>): Promise<APIResponse<T>> => {
  let attempts = Number(Config.get<string>('http_request_max_attempts'));

  return firstValueFrom(
    of(1).pipe(
      exhaustMap(() => request()),
      retryWhen(errors => {
        return errors.pipe(
          mergeMap(err => {
            if (--attempts > 0) {
              return of(err);
            } else {
              return throwError(() => err);
            }
          }),
        );
      }),
      map(async (resp: Response) => {
        let data: T = await resp.text().catch(err => {
          console.error(err);
          return null;
        });
        try {
          if (typeof data === 'string') {
            if (data.trim().length === 0) {
              data = null;
            } else {
              data = JSON.parse(data);
            }
          }
        } catch {
          // Do nothing since the exchange token endpoint does not return json
        }
        let message = undefined;
        if (data) {
          message = (data as any).message;
          delete (data as any).message;
        }
        return { status: resp.status, data: data, ...(message ? { message } : {}) };
      }),
      catchError(err => {
        return of({ error: true, message: err.message });
      }),
    ),
  );
};

export const withUnauthorizedResponseHandler = async <T>(request: Request<APIResponse<T>>): Promise<APIResponse<T>> => {
  return request().then(resp => {
    if (resp.status === 401) {
      // Refresh token endpoint is not available anymore
      // return handleInvalidLongTermToken(request);
      longTermToken.clearToken();
      systemError.publishCriticalError({ error: 'Invalid long term token' });
    }
    return resp;
  });
};

/*
// Info: Keeping this code as we will likely need it for SSO 2.0
let isRefreshing = false;
const refreshTokenGate = new BehaviorSubject(true);
const openGate = () =>
  refreshTokenGate.pipe(
    filter(open => open === true),
    take(1),
  );

const handleInvalidLongTermToken = async (request: Request<APIResponse>): Promise<APIResponse> => {
  if (isRefreshing) {
    return new Promise<APIResponse>(resolve => {
      openGate().subscribe(() => {
        resolve(request());
      });
    });
  } else {
    isRefreshing = true;
    const token = longTermToken.invalidateToken();
    refreshTokenGate.next(false);
    return refreshLongTermToken(token)
      .then((resp: APIResponse) => {
        isRefreshing = false;
        if (resp.status === 200) {
          longTermToken.publishValidToken(resp.data.token);
          const promise = request(); // Dispatch this request before opening the gate
          refreshTokenGate.next(true);
          return promise;
        } else {
          throw new Error();
        }
      })
      .catch(() => {
        longTermToken.clearToken();
        systemError.publishCriticalError({ error: 'Failed to refresh long term token' });
        return { error: true };
      });
  }
};
*/
